import MuiTable from '@mui/material/Table';
import type { ReactNode } from 'react';

export interface TableProps {
  size?: 'small' | 'medium';
  // Pins the header while the body scrolls — for a table inside a fixed-height,
  // internally-scrolling region (e.g. a long activity log).
  stickyHeader?: boolean;
  // Deterministic column sizing: 'fixed' makes the browser honour columnWidths
  // literally instead of renegotiating them against the cell content.
  layout?: 'auto' | 'fixed';
  // One entry per column, in order — any CSS width ('120px', '24%', a calc()).
  // Rendered as the <colgroup> a table needs as its first child, so callers never
  // hand-write table structure.
  columnWidths?: string[];
  'aria-label'?: string;
  children: ReactNode;
}

// Material Table behind the facade. The Panel renders small, pre-ordered, fixed-size
// lists, so the wrapper exposes only size, a sticky-header option, deterministic column
// sizing and an accessible label; chrome (sort/filter/paginate) stays out by design.
export function Table({
  size = 'small',
  stickyHeader = false,
  layout = 'auto',
  columnWidths,
  children,
  ...rest
}: TableProps) {
  const label = rest['aria-label'];
  return (
    <MuiTable
      size={size}
      stickyHeader={stickyHeader}
      {...(layout === 'fixed' ? { sx: { tableLayout: 'fixed' } } : {})}
      {...(label !== undefined ? { 'aria-label': label } : {})}
    >
      {columnWidths !== undefined && (
        <colgroup>
          {columnWidths.map((width, index) => (
            // A positional list of widths: the index IS the column's identity.
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
      )}
      {children}
    </MuiTable>
  );
}
